/**
 * QuizDemo.jsx — public Flash-Read & Retention Quiz at /quiz-demo.
 *
 * Visual language mirrors `/speed-test`: black HUD, red accent, gold for
 * XP rewards, serif display for the flashed words.
 *
 * Flow:
 *   INTRO     → pick a WPM tier (100..1000 in steps of 100)
 *   COUNTDOWN → 3-2-1-GO
 *   FLASH     → flash every word of the passage at the chosen wpm
 *   QUESTION  → ask the comprehension question
 *   (loop FLASH→QUESTION for each passage)
 *   RESULT    → retention % + XP + sign-up CTA / re-take button
 *
 * Anonymous players can take the test forever; XP only credits when the
 * viewer has a session cookie. POST /api/quiz-demo/submit handles both.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Sparkles, Trophy, ChevronRight, ArrowLeft, RotateCw, Brain, Square, Repeat, Pause,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Fixed-100 WPM tiers per Feb 9 2026 user spec. Keep these in sync with
// the backend's WPM_TIERS in `quiz_demo_routes.py`.
const WPM_TIERS = [
  { wpm: 100,  blurb: 'Schoolroom pace — recall should be perfect.' },
  { wpm: 200,  blurb: 'Average reader.' },
  { wpm: 300,  blurb: 'Above-average — most stall here.' },
  { wpm: 400,  blurb: 'Skim-and-grasp territory.' },
  { wpm: 500,  blurb: 'Speed reader trainee.' },
  { wpm: 600,  blurb: 'Most readers lose the thread.' },
  { wpm: 700,  blurb: 'Genuine speed reader pace.' },
  { wpm: 800,  blurb: 'Elite recall required.' },
  { wpm: 900,  blurb: 'Pro speed-readers only.' },
  { wpm: 1000, blurb: 'Almost subliminal — can you still answer?' },
];

// Chunk reading modes — per Feb 9 2026 user spec ("Add one test for
// chunk reading too"). Speed readers train this way: flashing 2-3 words
// at once forces the eye to grab phrases rather than syllable-by-syllable.
// The "test type" cycles through these in auto-shift mode.
const CHUNK_MODES = [
  { id: 1, label: 'Single word',  blurb: '1 word per flash · the classic test.' },
  { id: 2, label: 'Two-word chunks', blurb: 'Phrase-based reading drill.' },
  { id: 3, label: 'Three-word chunks', blurb: 'Pro speed-reader saccade pattern.' },
];

const Stage = {
  INTRO:     'intro',
  COUNTDOWN: 'countdown',
  FLASH:     'flash',
  QUESTION:  'question',
  RESULT:    'result',
};

const QuizDemo = () => {
  const navigate = useNavigate();
  const [search] = useSearchParams();
  const fromPostLogin = search.get('source') === 'postlogin';

  // ── State ──────────────────────────────────────────────────────
  const [stage, setStage] = useState(Stage.INTRO);
  const [wpm, setWpm] = useState(300);
  // Chunk size (1 / 2 / 3) — drives both the flash renderer and the
  // auto-shift cycle.
  const [chunkSize, setChunkSize] = useState(1);
  // Auto-cycle: when on, after each result screen the next chunk size
  // is selected and the next test starts automatically after a short
  // pause. User can hit "Stop cycle" any time.
  const [autoCycle, setAutoCycle] = useState(false);
  const [cyclePending, setCyclePending] = useState(0); // seconds remaining
  const [passages, setPassages] = useState([]); // [{id,passage,prompt,choices}]
  const [passageIdx, setPassageIdx] = useState(0);
  const [flashIdx, setFlashIdx] = useState(0);   // current CHUNK index (not word)
  const [countdown, setCountdown] = useState(3);
  const [answers, setAnswers] = useState({});   // { p1: 2, p2: 0 }
  // Feb 12 2026 user spec — Comprehension test with 10/20/30 questions
  // (configurable). Picking 5 stays as the legacy "quick" option.
  const [questionCount, setQuestionCount] = useState(5);
  const [pickedIdx, setPickedIdx] = useState(null);
  const [result, setResult] = useState(null);
  const [resultDisplay, setResultDisplay] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [loadingPassages, setLoadingPassages] = useState(false);
  const isLoggedInRef = useRef(false);
  // Track latest chunkSize so auto-cycle can mutate it without going
  // through the React render cycle (avoids stale closure on timers).
  const chunkSizeRef = useRef(chunkSize);
  useEffect(() => { chunkSizeRef.current = chunkSize; }, [chunkSize]);
  const autoCycleRef = useRef(autoCycle);
  useEffect(() => { autoCycleRef.current = autoCycle; }, [autoCycle]);

  // ── Boot: probe session (XP credit decision lives on backend) ──
  useEffect(() => {
    fetch(`${BACKEND_URL}/api/auth/me`, { credentials: 'include' })
      .then((r) => { isLoggedInRef.current = r.ok; })
      .catch(() => { isLoggedInRef.current = false; });
  }, []);

  // ── Start → fetch passages, countdown ──────────────────────────
  const startQuiz = async () => {
    setLoadingPassages(true);
    try {
      const r = await fetch(`${BACKEND_URL}/api/quiz-demo/passages?count=${questionCount}`);
      const d = await r.json();
      setPassages(d.passages || []);
    } catch {
      // Soft-fail: keep stage at INTRO so the user can retry.
      setLoadingPassages(false);
      return;
    }
    setLoadingPassages(false);
    setPassageIdx(0);
    setFlashIdx(0);
    setAnswers({});
    setResult(null); setResultDisplay(0);
    setPickedIdx(null);
    setCountdown(3);
    setStage(Stage.COUNTDOWN);
  };

  // ── Countdown effect ───────────────────────────────────────────
  useEffect(() => {
    if (stage !== Stage.COUNTDOWN) return;
    if (countdown <= 0) { setStage(Stage.FLASH); return; }
    const t = setTimeout(() => setCountdown((c) => c - 1), 700);
    return () => clearTimeout(t);
  }, [stage, countdown]);

  // ── Current passage / its words → broken into CHUNKS ──────────
  const currentPassage = passages[passageIdx] || null;
  const words = useMemo(
    () => (currentPassage?.passage || '').split(/\s+/).filter(Boolean),
    [currentPassage]
  );
  // Group the words into chunks of `chunkSize` each. Eg with chunkSize=3
  // and 8 words → [[w1 w2 w3] [w4 w5 w6] [w7 w8]].
  const chunks = useMemo(() => {
    if (!words.length) return [];
    const out = [];
    for (let i = 0; i < words.length; i += chunkSize) {
      out.push(words.slice(i, i + chunkSize).join(' '));
    }
    return out;
  }, [words, chunkSize]);

  // ── Flash effect ───────────────────────────────────────────────
  // Time per chunk = (60_000 / wpm) * chunkSize, so the user always
  // reads at their chosen wpm regardless of chunk size.
  useEffect(() => {
    if (stage !== Stage.FLASH) return;
    if (!chunks.length) { setStage(Stage.QUESTION); return; }
    if (flashIdx >= chunks.length) {
      setStage(Stage.QUESTION);
      return;
    }
    const msPerChunk = Math.max(80, Math.round((60_000 / wpm) * chunkSize));
    const timer = setTimeout(() => setFlashIdx((i) => i + 1), msPerChunk);
    return () => clearTimeout(timer);
  }, [stage, flashIdx, chunks, wpm, chunkSize]);

  // ── Submit final answers (after picking on last passage) ───────
  const submitAll = async (finalAnswers) => {
    setSubmitting(true);
    try {
      const payload = {
        wpm,
        chunk_size: chunkSize,
        answers: Object.entries(finalAnswers).map(([id, choice]) => ({ id, choice })),
      };
      const r = await fetch(`${BACKEND_URL}/api/quiz-demo/submit`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const d = await r.json();
      setResult(d);
    } catch {
      // Soft-fail with a local computed result so the user always
      // sees a score screen even if the network blips.
      const correct = passages.reduce((acc, p) => acc + (finalAnswers[p.id] === -1 ? 0 : 0), 0);
      setResult({
        correct,
        total: passages.length,
        retention_pct: Math.round((correct / passages.length) * 100),
        wpm,
        chunk_size: chunkSize,
        badge: 'Keep Practising',
        xp_award: 30,
        credited: false,
        anonymous_cta: 'Sign up to save your XP',
      });
    } finally {
      setSubmitting(false);
      setTimeout(() => setStage(Stage.RESULT), 600);
    }
  };

  // ── Pick an answer for the current passage ─────────────────────
  const onPick = (idx) => {
    if (pickedIdx !== null) return;
    setPickedIdx(idx);
    const passageId = currentPassage.id;
    const nextAnswers = { ...answers, [passageId]: idx };
    setAnswers(nextAnswers);
    // Show feedback for ~700ms then advance
    setTimeout(() => {
      setPickedIdx(null);
      if (passageIdx + 1 >= passages.length) {
        submitAll(nextAnswers);
      } else {
        setPassageIdx((p) => p + 1);
        setFlashIdx(0);
        setCountdown(2);
        setStage(Stage.COUNTDOWN);
      }
    }, 700);
  };

  // ── Result count-up animation (retention %) ────────────────────
  useEffect(() => {
    if (stage !== Stage.RESULT || !result) return;
    const target = result.retention_pct || 0;
    const start = performance.now();
    const dur = 1200;
    let raf;
    const tick = (now) => {
      const t = Math.min(1, (now - start) / dur);
      setResultDisplay(Math.round(target * (1 - Math.pow(1 - t, 3))));
      if (t < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [stage, result]);

  // ── Auto-cycle effect — when auto-cycle is on, the result screen
  // automatically rotates to the next chunk size after a short pause.
  // 1 → 2 → 3 → 1 → 2 … until the user clicks "Stop cycle".
  // The user spec was "Make shift from one speed test to another
  // automatic until user stops it" — so we treat the chunk-size mode
  // change as the "next test".
  useEffect(() => {
    if (stage !== Stage.RESULT) return;
    if (!autoCycleRef.current) { setCyclePending(0); return; }
    // Wait ~2s for the user to see the score animate, then count down
    // 5 seconds before auto-shifting.
    let remaining = 5;
    setCyclePending(remaining);
    const startDelay = setTimeout(() => {
      // Tick down every second.
      const interval = setInterval(() => {
        remaining -= 1;
        setCyclePending(remaining);
        if (remaining <= 0) {
          clearInterval(interval);
          // Only auto-shift if the user hasn't toggled off in the meantime.
          if (autoCycleRef.current) {
            const next = (chunkSizeRef.current % 3) + 1; // 1→2, 2→3, 3→1
            setChunkSize(next);
            // tiny delay so chunkSize state lands before startQuiz runs
            setTimeout(() => {
              if (autoCycleRef.current) startQuiz();
            }, 50);
          }
        }
      }, 1000);
      // Save the interval id on the closure so the cleanup below kills it.
      // eslint-disable-next-line no-param-reassign
      startDelay._interval = interval;
    }, 2000);
    return () => {
      clearTimeout(startDelay);
      if (startDelay._interval) clearInterval(startDelay._interval);
      setCyclePending(0);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage, result]);

  // Stop the auto-cycle without leaving the result page.
  const stopCycle = () => {
    setAutoCycle(false);
    setCyclePending(0);
  };

  // ──────────────────────────────────────────────────────────────
  return (
    <div
      className="min-h-screen w-full bg-[#0B0C10] text-[#EAE6DD] flex flex-col items-stretch overflow-x-hidden relative"
      style={{ fontFamily: '"Inter", system-ui, sans-serif' }}
      data-testid="quiz-demo-root"
    >
      {/* Top HUD bar */}
      <div className="px-5 py-4 flex items-center justify-between border-b border-white/5">
        <button
          type="button"
          onClick={() => fromPostLogin ? navigate('/') : navigate(-1)}
          className="inline-flex items-center gap-1 text-xs uppercase tracking-widest text-white/60 hover:text-white"
          data-testid="quiz-demo-back-btn"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          {fromPostLogin ? 'Skip' : 'Back'}
        </button>
        <span className="text-[10px] uppercase tracking-[0.3em] text-white/40">Flash Read · Retention Quiz</span>
        <span className="w-12" />
      </div>

      {/* Stage: INTRO */}
      {stage === Stage.INTRO && (
        <div className="flex-1 flex flex-col items-center justify-start px-6 py-10 gap-8" data-testid="quiz-demo-intro">
          <div className="text-center max-w-md">
            <p className="text-[#FF3B30] font-black text-xs uppercase tracking-[0.4em] mb-3">Challenge</p>
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl font-black leading-[1.05] text-white"
              style={{ fontFamily: '"Fraunces", "Playfair Display", Georgia, serif' }}
            >
              Read fast.{' '}
              <span className="text-[#FF3B30]">Remember faster.</span>
            </h1>
            <p className="text-sm text-white/60 mt-5">
              We&apos;ll flash a passage at your chosen speed and chunk size
              (1, 2, or 3 words at once), then test if you actually
              <strong className="text-white"> remember</strong> what you saw.
              5 passages, 5 questions.
            </p>
          </div>

          {/* WPM tier picker — increments of 100 */}
          <div className="w-full max-w-md" data-testid="quiz-demo-tier-picker">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Choose your speed</p>
            <div className="grid grid-cols-2 gap-2">
              {WPM_TIERS.map((t) => {
                const active = wpm === t.wpm;
                return (
                  <button
                    key={t.wpm}
                    type="button"
                    onClick={() => setWpm(t.wpm)}
                    className={`text-left rounded-xl border px-3 py-2 transition ${
                      active
                        ? 'border-[#FF3B30] bg-[#FF3B30]/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`quiz-demo-tier-${t.wpm}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base font-black">{t.wpm}</span>
                      <span className="text-[9px] uppercase tracking-widest text-white/40">wpm</span>
                    </div>
                    <p className="text-[10px] text-white/50 leading-tight mt-0.5 line-clamp-2">{t.blurb}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Chunk size picker — single word vs phrase-based flashes.
              Adds the "chunk reading" test mode requested Feb 9 2026. */}
          <div className="w-full max-w-md" data-testid="quiz-demo-chunk-picker">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">Chunk size</p>
            <div className="grid grid-cols-3 gap-2">
              {CHUNK_MODES.map((c) => {
                const active = chunkSize === c.id;
                return (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => setChunkSize(c.id)}
                    className={`text-left rounded-xl border px-3 py-2 transition ${
                      active
                        ? 'border-amber-400 bg-amber-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`quiz-demo-chunk-${c.id}`}
                  >
                    <div className="flex items-center justify-between">
                      <span className="text-base font-black">{c.id}<span className="text-[9px] uppercase tracking-widest text-white/40 ml-1">w</span></span>
                    </div>
                    <p className="text-[10px] text-white/50 leading-tight mt-0.5 line-clamp-2">{c.label}</p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Question-count picker — Feb 12 2026 user spec: "Test
              comprehension with 10, 20, 30 questions depending on
              users selection". Keeps 5 as the quick-start option. */}
          <div className="w-full max-w-md" data-testid="quiz-demo-question-count-picker">
            <p className="text-[10px] uppercase tracking-[0.3em] text-white/40 mb-2">How many questions?</p>
            <div className="grid grid-cols-4 gap-2">
              {[5, 10, 20, 30].map((n) => {
                const active = questionCount === n;
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => setQuestionCount(n)}
                    className={`text-center rounded-xl border px-3 py-3 transition ${
                      active
                        ? 'border-amber-400 bg-amber-400/10'
                        : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
                    }`}
                    data-testid={`quiz-demo-question-count-${n}`}
                  >
                    <span className="text-2xl font-black">{n}</span>
                    <p className="text-[10px] uppercase tracking-widest text-white/40 mt-0.5">
                      {n === 5 ? 'Quick' : n === 30 ? 'Marathon' : n === 20 ? 'Deep' : 'Standard'}
                    </p>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Auto-cycle toggle */}
          <label
            className={`w-full max-w-md flex items-start gap-3 rounded-xl border px-4 py-3 cursor-pointer transition ${
              autoCycle ? 'border-amber-400 bg-amber-400/10' : 'border-white/10 hover:border-white/30 bg-white/[0.02]'
            }`}
            data-testid="quiz-demo-auto-cycle-toggle"
          >
            <input
              type="checkbox"
              checked={autoCycle}
              onChange={(e) => setAutoCycle(e.target.checked)}
              className="mt-1 w-4 h-4 accent-amber-500"
              data-testid="quiz-demo-auto-cycle-input"
            />
            <div className="flex-1 min-w-0">
              <p className="text-sm font-bold text-white flex items-center gap-1">
                <Repeat className="w-3.5 h-3.5 text-amber-300" />
                Auto-shift between tests
              </p>
              <p className="text-[10px] text-white/55 mt-0.5">
                Automatically rotate 1-word → 2-word → 3-word chunks after each round, until you stop.
              </p>
            </div>
          </label>

          <button
            onClick={startQuiz}
            disabled={loadingPassages}
            className="bg-[#FF3B30] hover:bg-[#ff5046] disabled:opacity-60 text-white font-black px-8 py-4 rounded-full uppercase tracking-widest text-sm inline-flex items-center gap-2 shadow-[0_0_40px_rgba(255,59,48,0.4)]"
            data-testid="quiz-demo-start-btn"
          >
            {loadingPassages ? 'Loading…' : 'Start the quiz'}
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Stage: COUNTDOWN */}
      {stage === Stage.COUNTDOWN && (
        <div className="flex-1 flex flex-col items-center justify-center" data-testid="quiz-demo-countdown">
          <p className="text-[10px] uppercase tracking-[0.4em] text-white/40 mb-3">
            Passage {passageIdx + 1} of {passages.length}
          </p>
          <div
            className="text-[#FF3B30] font-black text-9xl sm:text-[12rem] tabular-nums animate-pulse"
            style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            key={countdown}
          >
            {countdown > 0 ? countdown : 'GO'}
          </div>
        </div>
      )}

      {/* Stage: FLASH */}
      {stage === Stage.FLASH && currentPassage && (
        <div className="flex-1 flex flex-col" data-testid="quiz-demo-flash">
          {/* Progress bar — per-passage, based on chunks (NOT raw words),
              so 1-word and 3-word modes both fill at a consistent rate. */}
          <div className="h-1.5 w-full bg-white/5">
            <div
              className="h-full bg-[#FF3B30] transition-[width] duration-100"
              style={{ width: `${(flashIdx / Math.max(1, chunks.length)) * 100}%` }}
            />
          </div>
          <div className="flex-1 flex items-center justify-center px-4">
            <span
              className={`font-black text-white text-center break-words ${
                chunkSize === 1 ? 'text-5xl sm:text-7xl md:text-8xl' :
                chunkSize === 2 ? 'text-4xl sm:text-6xl md:text-7xl' :
                                   'text-3xl sm:text-5xl md:text-6xl'
              }`}
              style={{ fontFamily: '"Fraunces", Georgia, serif', letterSpacing: '-0.02em' }}
              data-testid="quiz-demo-current-word"
            >
              {chunks[flashIdx] || ''}
            </span>
          </div>
          <div className="px-5 py-4 flex items-center justify-between">
            <span className="text-[10px] uppercase tracking-widest text-white/40">
              Passage {passageIdx + 1} / {passages.length} · {chunkSize}w chunks
            </span>
            <div className="font-mono text-right">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Reading at</div>
              <div className="text-3xl font-black text-[#FFD60A] tabular-nums" data-testid="quiz-demo-wpm-badge">{wpm}</div>
            </div>
          </div>
        </div>
      )}

      {/* Stage: QUESTION */}
      {stage === Stage.QUESTION && currentPassage && (
        <div className="flex-1 flex flex-col items-stretch justify-center px-5 py-8 gap-3" data-testid="quiz-demo-question">
          <div className="text-center mb-1">
            <p className="text-[10px] uppercase tracking-widest text-white/40">
              Retention check — passage {passageIdx + 1} of {passages.length}
            </p>
            <h2 className="text-xl sm:text-2xl font-black text-white mt-1 leading-snug max-w-2xl mx-auto">
              {currentPassage.prompt}
            </h2>
          </div>
          {currentPassage.choices.map((choice, i) => {
            const picked = pickedIdx === i;
            let border = 'border-white/15 hover:border-white/40';
            if (pickedIdx !== null) {
              if (picked) border = 'border-[#FFD60A] bg-[#FFD60A]/10';
              else border = 'border-white/10 opacity-60';
            }
            return (
              <button
                key={i}
                onClick={() => onPick(i)}
                disabled={pickedIdx !== null}
                className={`w-full text-left rounded-2xl border px-4 py-4 transition ${border}`}
                style={{ fontFamily: '"Fraunces", Georgia, serif' }}
                data-testid={`quiz-demo-choice-${i}`}
              >
                <span className="text-base sm:text-lg text-white">{choice}</span>
              </button>
            );
          })}
          {submitting && <p className="text-center text-xs text-white/50 mt-2">Scoring…</p>}
        </div>
      )}

      {/* Stage: RESULT */}
      {stage === Stage.RESULT && result && (
        <div className="flex-1 flex flex-col items-center justify-start px-5 py-8 gap-6" data-testid="quiz-demo-result">
          <div className="text-center mt-2">
            <p className="text-[10px] uppercase tracking-[0.4em] text-white/40">Retention</p>
            <div
              className="text-7xl sm:text-8xl font-black tabular-nums text-white mt-1"
              style={{ fontFamily: '"Fraunces", Georgia, serif' }}
            >
              {resultDisplay}
              <span className="text-2xl sm:text-3xl text-white/40 ml-2">%</span>
            </div>
            <p className="text-sm text-white/60 mt-1">
              {result.correct} / {result.total} correct at <strong className="text-[#FFD60A]">{result.wpm} wpm</strong>
              {' '}<span className="text-white/40">·</span>{' '}
              <strong className="text-amber-300">{chunkSize}w chunks</strong>
            </p>
          </div>

          {/* Auto-cycle banner — appears when cycleMode is on. Shows the
              countdown to the next test (next chunk size) and lets the
              user stop the cycle without leaving the result page. */}
          {autoCycle && cyclePending > 0 && (
            <div
              className="w-full max-w-md rounded-2xl border border-amber-400/40 bg-amber-400/10 px-4 py-3 flex items-center gap-3"
              data-testid="quiz-demo-cycle-banner"
            >
              <Repeat className="w-5 h-5 text-amber-300 flex-shrink-0" />
              <p className="text-xs text-white/80 flex-1 min-w-0">
                Next test starts in{' '}
                <strong className="text-amber-200 tabular-nums">{cyclePending}s</strong> —{' '}
                <span className="text-white/70">{(chunkSize % 3) + 1}-word chunks</span>
              </p>
              <button
                type="button"
                onClick={stopCycle}
                className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full bg-white/10 hover:bg-white/15 text-white text-[11px] font-bold uppercase tracking-widest"
                data-testid="quiz-demo-stop-cycle-btn"
              >
                <Pause className="w-3 h-3" /> Stop
              </button>
            </div>
          )}

          {/* Comparison bars: you vs typical retention at this wpm */}
          <div className="w-full max-w-md space-y-2" data-testid="quiz-demo-comparison-bars">
            {[
              { label: 'You',                       pct: result.retention_pct,  color: '#FF3B30' },
              { label: 'Average reader at this wpm', pct: Math.max(20, 100 - Math.round((result.wpm - 200) / 8)), color: '#6B7280' },
              { label: 'Top 1% (any wpm)',           pct: 95,                       color: '#FFD60A' },
            ].map((row) => (
              <div key={row.label} className="text-xs">
                <div className="flex justify-between mb-0.5">
                  <span className="text-white/70">{row.label}</span>
                  <span className="font-mono tabular-nums text-white/50">{Math.max(0, Math.min(100, row.pct))}%</span>
                </div>
                <div className="h-2.5 w-full bg-white/5 rounded-full overflow-hidden">
                  <div
                    className="h-full rounded-full transition-[width] duration-700"
                    style={{ width: `${Math.max(0, Math.min(100, row.pct))}%`, background: row.color }}
                  />
                </div>
              </div>
            ))}
          </div>

          {/* XP badge */}
          <div
            className="w-full max-w-md rounded-2xl border border-[#FFD60A]/40 bg-[#FFD60A]/10 px-5 py-4 flex items-center gap-3"
            data-testid="quiz-demo-xp-badge"
          >
            <div className="w-12 h-12 rounded-full bg-[#FFD60A]/25 grid place-items-center">
              <Brain className="w-6 h-6 text-[#FFD60A]" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-[10px] uppercase tracking-widest text-[#FFD60A]/80">
                {result.credited ? 'XP credited to your account' : 'XP available'}
              </p>
              <p className="text-base font-black text-white">+{result.xp_award} XP — {result.badge}</p>
              {!result.credited && result.anonymous_cta && (
                <p className="text-[11px] text-white/60 mt-0.5">{result.anonymous_cta}</p>
              )}
              {result.new_level && (
                <p className="text-[11px] text-emerald-200 mt-0.5">Now at level {result.new_level} ✨</p>
              )}
            </div>
          </div>

          {/* CTAs */}
          <div className="w-full max-w-md grid grid-cols-1 sm:grid-cols-2 gap-2 mt-1">
            {result.credited ? (
              <button
                onClick={() => navigate('/')}
                className="bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
                data-testid="quiz-demo-return-app-btn"
              >
                Read more on Smarter You <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <button
                onClick={() => navigate('/login?signup=1')}
                className="bg-[#FF3B30] hover:bg-[#ff5046] text-white font-black py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
                data-testid="quiz-demo-signup-btn"
              >
                Sign up & save my XP <Sparkles className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={startQuiz}
              className="border border-white/20 text-white hover:bg-white/5 font-bold py-3 rounded-full uppercase tracking-widest text-xs inline-flex items-center justify-center gap-2"
              data-testid="quiz-demo-retry-btn"
            >
              <RotateCw className="w-4 h-4" /> Try again
            </button>
          </div>

          {/* Cross-link to /speed-test */}
          <button
            type="button"
            onClick={() => navigate('/speed-test')}
            className="text-[11px] uppercase tracking-widest text-white/40 hover:text-white inline-flex items-center gap-1"
            data-testid="quiz-demo-speed-test-link"
          >
            <Trophy className="w-3 h-3" /> Test pure speed instead
          </button>
        </div>
      )}
    </div>
  );
};

export default QuizDemo;
